import React, { useContext, useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as yup from 'yup';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ProjectContainer from '../../components/containers/ProjectContainer';
import { getBannerOffers, setBannerOffers } from '../../api/bannerOffers';
import { BannerEntry, BannerEvent, BannerOffers as BannerOffersType } from '../../api/bannerOffers/types';
import { ActivityIndicatorContext } from '../../providers/ActivityIndicatorProvider/context';
import { ToastContext } from '../../providers/ToastProvider/context';
import { isProjectError } from '../../utils/errors';
import defaultsJson from './defaults.json';
import EventModal from './components/EventModal';
import EventsList from './components/EventsList';
import { addDaysToLocalString, MAX_CTA_LENGTH, MAX_LABEL_LENGTH, sortEventsByStart } from './utils';

const DEFAULTS = defaultsJson as BannerOffersType;

const entrySchema = yup.object({
  emoji: yup.string().trim().required('Emoji is required'),
  label: yup.string().trim().required('Label is required').max(MAX_LABEL_LENGTH, `Max ${MAX_LABEL_LENGTH} characters`),
  cta: yup.string().trim().required('CTA is required').max(MAX_CTA_LENGTH, `Max ${MAX_CTA_LENGTH} characters`),
});

const schema = yup.object({
  enabled: yup.boolean().required(),
  drop: yup.object({
    minutes: yup.number().typeError('Enter a number').integer('Whole number').positive('Must be positive').required(),
    cooldownHours: yup
      .number()
      .typeError('Enter a number')
      .integer('Whole number')
      .positive('Must be positive')
      .required(),
  }),
  default: entrySchema,
  weekly: yup.object({ sunday: entrySchema, monday: entrySchema }),
});

const withSortedEvents = (data: BannerOffersType): BannerOffersType => ({
  ...data,
  events: sortEventsByStart(data.events || []),
});

const BannerOffers = () => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showSuccessToast, showErrorToast } = useContext(ToastContext);

  const [initialValues, setInitialValues] = useState<BannerOffersType | undefined>();
  const [isSaved, setIsSaved] = useState(true);
  const [formKey, setFormKey] = useState(0);
  const [eventModal, setEventModal] = useState<{ index: number | null; draft?: BannerEvent } | undefined>();
  const [deleteIndex, setDeleteIndex] = useState<number | undefined>();

  useEffect(() => {
    setIsLoading(true);
    getBannerOffers().then((res) => {
      const error = isProjectError(res);
      if (error) {
        showErrorToast(error);
      } else if (res) {
        setInitialValues(withSortedEvents(res as BannerOffersType));
      } else {
        setIsSaved(false);
        setInitialValues(withSortedEvents(DEFAULTS));
      }
      setIsLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSave = async (values: BannerOffersType) => {
    const payload: BannerOffersType = {
      enabled: values.enabled,
      drop: { minutes: Number(values.drop.minutes), cooldownHours: Number(values.drop.cooldownHours) },
      events: sortEventsByStart(values.events),
      weekly: { sunday: trimEntry(values.weekly.sunday), monday: trimEntry(values.weekly.monday) },
      default: trimEntry(values.default),
    };
    setIsLoading(true);
    const result = await setBannerOffers(payload);
    setIsLoading(false);
    const error = isProjectError(result);
    if (error) {
      showErrorToast(error);
    } else {
      showSuccessToast('Banner offers saved');
      setIsSaved(true);
      setInitialValues(withSortedEvents(payload));
      setFormKey((k) => k + 1);
    }
  };

  if (!initialValues) {
    return <ProjectContainer title={'Banner Offers'} />;
  }

  return (
    <ProjectContainer title={'Banner Offers'}>
      <Formik
        key={formKey}
        initialValues={initialValues}
        validationSchema={schema}
        enableReinitialize
        onSubmit={onSave}>
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue, handleSubmit, dirty }) => {
          const err = (path: string): string | undefined => {
            const get = (o: any) => path.split('.').reduce((a, k) => a?.[k], o);
            return get(errors) && get(touched) ? get(errors) : undefined;
          };

          const entryFields = (path: 'default' | 'weekly.sunday' | 'weekly.monday', title: string) => (
            <Paper variant='outlined' sx={{ p: 2 }}>
              <Typography variant='subtitle1' component='h3' gutterBottom>
                {title}
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                {(['emoji', 'label', 'cta'] as const).map((field) => {
                  const get = (o: any) => `${path}.${field}`.split('.').reduce((a, k) => a?.[k], o);
                  const max = field === 'label' ? MAX_LABEL_LENGTH : field === 'cta' ? MAX_CTA_LENGTH : undefined;
                  return (
                    <TextField
                      key={field}
                      size='small'
                      name={`${path}.${field}`}
                      label={field === 'cta' ? 'CTA' : field[0].toUpperCase() + field.slice(1)}
                      value={get(values) ?? ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(err(`${path}.${field}`))}
                      helperText={err(`${path}.${field}`) || (max ? `${(get(values) || '').length}/${max}` : undefined)}
                      sx={{ flex: field === 'label' ? 2 : 1 }}
                    />
                  );
                })}
              </Stack>
            </Paper>
          );

          const isEditing = eventModal?.index !== null && eventModal?.index !== undefined && !eventModal.draft;
          const modalEvent = eventModal
            ? eventModal.draft ?? (eventModal.index === null ? null : values.events[eventModal.index])
            : undefined;

          const onEventSave = (event: BannerEvent) => {
            const next = [...values.events];
            if (!isEditing || eventModal?.index === null || eventModal?.index === undefined) {
              next.push(event);
            } else {
              next[eventModal.index] = event;
            }
            setFieldValue('events', sortEventsByStart(next));
            setEventModal(undefined);
          };

          const onDuplicate = (index: number) => {
            const source = values.events[index];
            setEventModal({
              index: null,
              draft: {
                ...source,
                start: addDaysToLocalString(source.start, 365),
                end: addDaysToLocalString(source.end, 365),
              },
            });
          };

          return (
            <Box component='form' onSubmit={handleSubmit} noValidate sx={{ overflowY: 'auto', height: '100%' }}>
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  bgcolor: 'background.paper',
                  borderBottom: 1,
                  borderColor: 'divider',
                  px: 3,
                  py: 1.5,
                }}>
                <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap'>
                  <Box flex={1} minWidth={220}>
                    <Typography variant='h6' component='h1'>
                      Banner offers
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Schedule the promo banner shown in the app.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch checked={values.enabled} onChange={(e) => setFieldValue('enabled', e.target.checked)} />
                    }
                    label={values.enabled ? 'Banners on' : 'Banners off'}
                  />
                  {!isSaved || dirty ? (
                    <Chip size='small' color='warning' label={isSaved ? 'Unsaved changes' : 'Not saved yet'} />
                  ) : null}
                  <Button type='submit' variant='contained' disabled={isSaved && !dirty}>
                    Save changes
                  </Button>
                </Stack>
              </Box>

              <Stack spacing={3} sx={{ p: 3 }}>
                {!isSaved ? (
                  <Alert severity='info'>
                    No saved config yet. These are the defaults. Press Save to publish them.
                  </Alert>
                ) : null}
                {!values.enabled ? (
                  <Alert severity='warning'>Banners are off. Nothing is shown in the app until you turn them on.</Alert>
                ) : null}

                <EventsList
                  events={values.events}
                  onAdd={() => setEventModal({ index: null })}
                  onEdit={(index) => setEventModal({ index })}
                  onDuplicate={onDuplicate}
                  onDelete={setDeleteIndex}
                />

                <Accordion variant='outlined' disableGutters>
                  <AccordionSummary>
                    <Box>
                      <Typography variant='subtitle1' component='h2'>
                        Everyday banners
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        Used on days with no event: Sunday, Monday and all other days.
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      {entryFields('default', 'Default (all other days)')}
                      {entryFields('weekly.sunday', 'Sunday')}
                      {entryFields('weekly.monday', 'Monday')}
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                <Accordion variant='outlined' disableGutters>
                  <AccordionSummary>
                    <Box>
                      <Typography variant='subtitle1' component='h2'>
                        Countdown timing
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {values.drop.minutes} min countdown · {values.drop.cooldownHours} h cooldown
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        size='small'
                        type='number'
                        name='drop.minutes'
                        label='Countdown length (minutes)'
                        value={values.drop.minutes}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(err('drop.minutes'))}
                        helperText={err('drop.minutes') || 'How long the banner countdown runs (default 20)'}
                        inputProps={{ min: 1, step: 1 }}
                        fullWidth
                      />
                      <TextField
                        size='small'
                        type='number'
                        name='drop.cooldownHours'
                        label='Cooldown (hours)'
                        value={values.drop.cooldownHours}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(err('drop.cooldownHours'))}
                        helperText={err('drop.cooldownHours') || 'Hidden after it expires (default 6)'}
                        inputProps={{ min: 1, step: 1 }}
                        fullWidth
                      />
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                <Typography variant='caption' color='text.secondary'>
                  Rule: on the user&apos;s local date the shortest overlapping event wins (ties = list order). Otherwise
                  Sunday or Monday banner, else the default. Every banner opens the same $39 / 3-month paywall.
                </Typography>
              </Stack>

              <EventModal
                event={modalEvent}
                isEdit={isEditing}
                otherEvents={values.events.filter((_, i) => !isEditing || i !== eventModal?.index)}
                onClose={() => setEventModal(undefined)}
                onSave={onEventSave}
              />

              <Dialog open={deleteIndex !== undefined} onClose={() => setDeleteIndex(undefined)}>
                <DialogTitle>Delete event?</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    {deleteIndex !== undefined ? values.events[deleteIndex]?.label : ''} will be removed once you save.
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setDeleteIndex(undefined)}>Cancel</Button>
                  <Button
                    color='error'
                    onClick={() => {
                      setFieldValue(
                        'events',
                        values.events.filter((_, i) => i !== deleteIndex),
                      );
                      setDeleteIndex(undefined);
                    }}>
                    Delete
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          );
        }}
      </Formik>
    </ProjectContainer>
  );
};

const trimEntry = (entry: BannerEntry): BannerEntry => ({
  emoji: entry.emoji.trim(),
  label: entry.label.trim(),
  cta: entry.cta.trim(),
});

export default BannerOffers;
